import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlass, X, CaretRight, Lightning } from '@phosphor-icons/react';
import API from '@/lib/api';
import { useCompanyFunctions } from '@/contexts/CompanyFunctionsContext';

/**
 * Globale command-palette / omnibar.
 * Open via Cmd+K (macOS) of Ctrl+K (Windows/Linux).
 * Zoekt over klanten, artikels, documenten, bestelbonnen en verkopers.
 */
export default function CommandPalette() {
  const { isVisible } = useCompanyFunctions();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  // Toetsen-listener voor Cmd+K / Ctrl+K + custom event vanaf topbar-knop
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen(v => !v);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    const openHandler = () => setOpen(true);
    window.addEventListener('keydown', handler);
    window.addEventListener('open-cmdk', openHandler);
    return () => {
      window.removeEventListener('keydown', handler);
      window.removeEventListener('open-cmdk', openHandler);
    };
  }, []);

  // Focus input bij openen
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setCursor(0);
    } else {
      setQuery(''); setResults([]);
    }
  }, [open]);

  // Debounced zoek
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await API.get(`/search?q=${encodeURIComponent(query)}`);
        setResults((r.data?.results || []).filter(result => isVisible(
          result.document_type ? `/documenten?type=${encodeURIComponent(result.document_type)}` : result.url
        )));
        setCursor(0);
      } catch {
        setResults([]);
      } finally { setLoading(false); }
    }, 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, isVisible]);

  const go = useCallback((r) => {
    setOpen(false);
    navigate(r.url);
  }, [navigate]);

  // Keyboard navigatie binnen resultaten
  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
    else if (e.key === 'Enter' && results[cursor]) { e.preventDefault(); go(results[cursor]); }
  };

  // Groep resultaten per groep-label
  const groupen = results.reduce((acc, r) => {
    const g = r.groep || 'Overig';
    if (!acc[g]) acc[g] = [];
    acc[g].push(r);
    return acc;
  }, {});

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/50 backdrop-blur-sm flex items-start justify-center pt-20"
      onClick={() => setOpen(false)}
      data-testid="command-palette"
    >
      <div
        className="w-full max-w-2xl bg-white shadow-2xl rounded-2xl overflow-hidden"
        style={{ border: '1px solid rgba(0,0,0,0.06)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Zoek-invoer */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
          <MagnifyingGlass size={20} className="text-gray-400" weight="bold" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={onKey}
            placeholder="Zoek klanten, artikels, facturen..."
            className="flex-1 border-0 focus:outline-none text-[15px] font-medium bg-transparent"
            data-testid="cmdk-input"
          />
          {loading && <Lightning size={16} weight="bold" className="text-gray-400 animate-pulse" />}
          <kbd className="text-[10px] px-1.5 py-0.5 bg-gray-100 border border-gray-200 rounded font-mono text-gray-500">ESC</kbd>
          <button onClick={() => setOpen(false)} className="flex items-center justify-center" style={{ width: 28, height: 28 }}>
            <X size={16} className="text-gray-400 hover:text-gray-700" />
          </button>
        </div>

        {/* Resultaten */}
        <div className="max-h-[60vh] overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
          {query.length < 2 ? (
            <div className="p-8 text-center">
              <div className="text-[13px] text-gray-500 mb-3">Begin te typen om te zoeken...</div>
              <div className="flex items-center justify-center gap-4 text-[11px] text-gray-400">
                <span className="inline-flex items-center gap-1"><kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-200 rounded font-mono">↑↓</kbd> navigeren</span>
                <span className="inline-flex items-center gap-1"><kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-200 rounded font-mono">Enter</kbd> openen</span>
              </div>
              <div className="mt-4 text-[10px] text-gray-400 italic">Tip: druk <kbd className="px-1 bg-gray-100 border border-gray-200 rounded font-mono">Cmd+K</kbd> (macOS) of <kbd className="px-1 bg-gray-100 border border-gray-200 rounded font-mono">Ctrl+K</kbd> voor snelle zoek</div>
            </div>
          ) : results.length === 0 && !loading ? (
            <div className="p-8 text-center text-[13px] text-gray-500 italic">Geen resultaten voor &quot;{query}&quot;</div>
          ) : (
            (() => {
              let idx = -1;
              return Object.entries(groupen).map(([groep, items]) => (
                <div key={groep}>
                  <div className="px-4 py-1.5 text-[10px] uppercase tracking-wider font-bold text-gray-500 bg-gray-50 sticky top-0">{groep}</div>
                  {items.map(r => {
                    idx++;
                    const active = idx === cursor;
                    return (
                      <button
                        key={`${r.type}-${r.url}-${idx}`}
                        onClick={() => go(r)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${active ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                        data-testid={`cmdk-result-${idx}`}
                      >
                        <span className="text-lg shrink-0">{r.icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-semibold text-gray-900 truncate">{r.titel}</div>
                          {r.subtitel && <div className="text-[11px] text-gray-500 truncate">{r.subtitel}</div>}
                        </div>
                        <CaretRight size={14} weight="bold" className={active ? 'text-[#002FA7]' : 'text-gray-300'} />
                      </button>
                    );
                  })}
                </div>
              ));
            })()
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 flex items-center justify-between text-[10px] text-gray-500">
          <span>Globale zoek over alle modules</span>
          <span>{results.length > 0 ? `${results.length} resultaten` : ''}</span>
        </div>
      </div>
    </div>
  );
}
